import React, { useContext } from 'react';
import { observer } from 'mobx-react';
import {
  AiAssistSelectionTools as UiAiAssistSelectionTools,
  useFeature,
} from '@ole-ui/ole-ui-components';
import { aiAssistantContext } from 'components/Course/AiAssist/store';
import { EnablementFeatureFlags } from 'config/featureFlags';
import useMobxStores from 'hooks/useMobxStores';

type AiAssistSelectionToolsProps = {
  containerRef: React.RefObject<HTMLElement>;
  isAiQuickMenuEnabled?: boolean;
  setIsAiQuickMenuEnabled?: (isEnabled: boolean) => void;
  /**
   * Temporarily suppress the floating quick menu while other course HUD sidebars/overlays are open.
   * This should not affect the persisted "enabled" preference.
   */
  isAiQuickMenuSuppressed?: boolean;
};

const AiAssistSelectionTools: React.FC<AiAssistSelectionToolsProps> = ({
  containerRef,
  isAiQuickMenuEnabled = true,
  setIsAiQuickMenuEnabled,
  isAiQuickMenuSuppressed = false,
}) => {
  const aiAssistantStore = useContext(aiAssistantContext);
  const { userStore } = useMobxStores();

  const aiDisclaimerAccepted = Boolean(
    userStore.userSettings?.ai_assistant_disclaimer_accepted,
  );
  const isAiAssistElaborateEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.AIAssistantElaborateEnabled,
    defaultFlagValue: false,
  });

  const MIN_CONTEXT_CHARS = 250;

  if (!aiAssistantStore) return null;

  return (
    <UiAiAssistSelectionTools
      containerRef={containerRef}
      minContextChars={MIN_CONTEXT_CHARS}
      isSidebarOpen={aiAssistantStore.isOpen}
      isQuickMenuSuppressed={isAiQuickMenuSuppressed}
      isQuickMenuEnabled={isAiQuickMenuEnabled}
      onQuickMenuEnabledChange={setIsAiQuickMenuEnabled}
      onSelectionChange={(selectionText) =>
        aiAssistantStore.setSelectedText(selectionText)
      }
      onRecommend={(selectionText) => {
        if (!aiDisclaimerAccepted) {
          aiAssistantStore.setPendingRecommendationText(selectionText);
          aiAssistantStore.setIsOpen(true);
        } else {
          aiAssistantStore.requestRecommendations(selectionText);
        }
      }}
      onSummarize={(selectionText) => {
        if (!aiDisclaimerAccepted) {
          aiAssistantStore.setPendingSummaryText(selectionText);
          aiAssistantStore.setIsOpen(true);
        } else {
          aiAssistantStore.requestSummary(selectionText);
        }
      }}
      onElaborate={
        isAiAssistElaborateEnabled
          ? (selectionText) => {
              aiAssistantStore.setSelectedText(selectionText);
              if (!aiDisclaimerAccepted) {
                aiAssistantStore.setPendingElaborationText(selectionText);
                aiAssistantStore.setIsOpen(true);
              } else {
                aiAssistantStore.requestElaboration(selectionText);
              }
            }
          : undefined
      }
    />
  );
};

export default observer(AiAssistSelectionTools);
